import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const RenderAnswerChoices = ({ answer_bank, exampleQuestion }) => (
  <div className="exercise-card">
    <h3 className="font-display font-bold text-xl text-slate-800 dark:text-white flex items-center gap-2 mb-6">
      <span className="material-symbols-outlined text-primary-blue">grid_view</span>
      Answer Choices
    </h3>
    <div className="space-y-3">
      {Object.entries(answer_bank).map(([letter, text]) => {
        const isExample = exampleQuestion && exampleQuestion.accepted_answers.includes(letter);
        return (
          <div key={letter} className={`flex items-start gap-3 p-2 rounded-lg ${isExample ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
            <span className="font-display font-bold text-primary-blue text-sm">{letter}</span>
            <p className="font-body text-sm text-gray-700 dark:text-gray-300 m-0">
              {text.replace(/\(Example\)/gi, '').trim()}
              {isExample && <span className="font-bold text-primary-blue text-[10px] ml-2 uppercase">(Example)</span>}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

const DialogueLine = ({ character, text, question, isExample = false, answer, isHydrated, isReviewMode, showHint, handleAnswerChange, character_1, character_2 }) => {
  const isResponder = character.name === character_2.name;
  const isCorrect = isReviewMode && (question?.is_correct === true || question?.is_correct === 'true');
  const showIncorrectAnswer = isReviewMode && !isExample && !isCorrect && question;

  const avatarIcon = isResponder ? (character_2.avatar || 'chat_bubble') : (character_1.avatar || 'person');

  return (
    <div className="flex items-start gap-3">
      {/* Compact avatar */}
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white shadow-sm mt-0.5 ${isResponder ? 'bg-teal-400' : 'bg-indigo-400'}`}>
        <span className="material-symbols-outlined text-lg">{avatarIcon}</span>
      </div>

      {text ? (
        <div className="bg-slate-50 dark:bg-slate-700/30 px-4 py-2.5 rounded-2xl rounded-tl-none border border-slate-100 dark:border-slate-600 flex-1">
          <p className="m-0 text-sm md:text-base font-medium text-slate-700 dark:text-slate-200 leading-relaxed">{text}</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-1 py-1">
          <span className="font-black text-slate-300 tracking-[0.2em] text-xs">.......</span>
          {showIncorrectAnswer ? (
            <div className="exercise-input-text inline-flex items-center bg-red-50 px-3 py-1 rounded-xl border border-red-100 h-11 min-w-[3rem] justify-center mx-0.5 shadow-sm">
              <span style={{ textDecoration: 'line-through' }} className="text-red-500 opacity-60 text-sm mr-2 font-bold">{question.user_answer?.answer || '?'}</span>
              <span className="text-green-600 font-black text-lg">{question.accepted_answers[0]}</span>
            </div>
          ) : (
            <input
              type="text"
              maxLength="1"
              autoComplete="off"
              className={`exercise-input-field w-11 h-11 text-lg font-black !rounded-xl mx-0.5 shadow-inner border-2 transition-all text-center ${isExample ? 'border-hupath-scout bg-hupath-scout/5 text-hupath-scout' : 'border-slate-200 focus:border-hupath-scout focus:ring-4 focus:ring-hupath-scout/10'} ${showHint && !isExample ? 'hint-input' : ''}`}
              value={isExample ? question.accepted_answers[0] : (showHint || isCorrect ? question.accepted_answers[0] : answer || '')}
              onChange={(e) => handleAnswerChange(question.id, e)}
              placeholder="?"
              disabled={isReviewMode || showHint || !isHydrated}
            />
          )}
          <span className="font-black text-slate-300 tracking-[0.2em] text-xs">.......</span>
          {isReviewMode && (
            <>
              <div className={`ml-2 w-6 h-6 rounded-full flex items-center justify-center text-white ${isCorrect ? 'bg-hupath-leaf' : 'bg-red-500'}`}>
                <span className="material-symbols-outlined text-sm font-bold">{isCorrect ? 'check' : 'close'}</span>
              </div>
              {!isExample && <QuestionLearnButton question={question} />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const RenderConversation = ({ exampleQuestion, mainQuestions, answers, isHydrated, isReviewMode, showHint, handleAnswerChange, character_1, character_2 }) => {
  const allItems = exampleQuestion ? [{ ...exampleQuestion, isExample: true }, ...mainQuestions] : mainQuestions;
  return (
    <div className="space-y-4">
      {allItems.map((q) => (
        <div
          key={q.id}
          className={`bg-white dark:bg-slate-800/40 rounded-3xl border-2 transition-all p-4 md:p-6 relative flex items-start gap-4 ${q.isExample
            ? 'border-hupath-scout/20 bg-hupath-scout/[0.02]'
            : 'border-slate-100 dark:border-slate-700 hover:border-slate-200 shadow-sm'
          }`}
        >
          {/* Number badge on the left */}
          <div className="shrink-0 mt-1">
            {q.isExample
              ? <span className="font-black text-white bg-hupath-scout px-2 py-1 rounded-lg text-[10px] uppercase tracking-wider shadow-sm">e.g.</span>
              : <span className="font-black text-slate-400 bg-slate-50 dark:bg-slate-900 px-2 py-1 rounded-lg text-xs border border-slate-100 dark:border-slate-800 shadow-sm">{q.order_index}.</span>
            }
          </div>

          {/* Dialogue content stacked */}
          <div className="flex-1 flex flex-col gap-4 min-w-0">
            <DialogueLine character={character_1} text={q.question_text.replace(/\(Example\)/gi, '').trim()} character_1={character_1} character_2={character_2} />
            <DialogueLine
              character={character_2}
              question={q}
              answer={answers[q.id]}
              isExample={q.isExample || false}
              isHydrated={isHydrated}
              isReviewMode={isReviewMode}
              showHint={showHint}
              handleAnswerChange={handleAnswerChange}
              character_1={character_1}
              character_2={character_2}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const ReadingPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);


  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    answer_bank,
    character_1,
    character_2,
  } = exerciseData.content_data;

  const exampleQuestion = exerciseData.questions.find(q => q.order_index === 0);
  const mainQuestions = exerciseData.questions.filter(q => q.order_index !== 0).sort((a, b) => a.order_index - b.order_index);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleAnswerChange = (questionId, event) => {
    const typedAnswer = event.target.value.toUpperCase();
    setAnswers(prev => ({ ...prev, [questionId]: typedAnswer }));
  };

  return (
    <div className="w-full h-full p-4 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-5xl mx-auto bg-white dark:bg-slate-900/50 rounded-[2.5rem] p-6 md:p-10 shadow-[0px_20px_50px_rgba(0,0,0,0.05)] border border-slate-100 dark:border-slate-800 my-4">
        {image_url && (
          <div className="bg-slate-50 dark:bg-card-dark p-3 rounded-[2rem] border-2 border-white dark:border-card-dark overflow-hidden shrink-0 mb-10 shadow-sm max-w-2xl mx-auto">
            <img
              src={`${ASSET_BASE_URL}${image_url}`}
              alt="Exercise Context"
              className="mx-auto rounded-2xl w-auto max-w-full max-h-64 object-contain"
            />
          </div>
        )}
        <RenderConversation
          exampleQuestion={exampleQuestion}
          mainQuestions={mainQuestions}
          answers={answers}
          isHydrated={isHydrated}
          isReviewMode={isReviewMode}
          showHint={showHint}
          handleAnswerChange={handleAnswerChange}
          character_1={character_1}
          character_2={character_2}
        />
      </div>
    </div>
  );
};


export default ReadingPart2;